import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';

interface SubscriptionView {
  plan: string;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndsAt: string | null;
  canceledAt: string | null;
  trialExtensionCount: number;
  features: string[];
  paymentHistory: Array<{ date: string; amount: number; status: string }>;
}

const TRIAL_STATUSES = ['trialing'];
const REACTIVATABLE_STATUSES = ['expired', 'grace_period'];
const MAX_EXTENSIONS = 3;
const AVAILABLE_PLANS = ['start', 'business', 'pro'];

function getStatusColor(status: string) {
  switch (status) {
    case 'trialing': return 'blue';
    case 'active': return 'green';
    case 'expired': return 'red';
    case 'canceled': return 'red';
    case 'past_due': return 'orange';
    case 'grace_period': return 'yellow';
    case 'paused': return 'gray';
    default: return 'gray';
  }
}

export function SubscriptionPanel({ organizationId }: { organizationId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const textColor = useColorModeValue('navy.700', 'white');
  const secondaryTextColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const formBg = useColorModeValue('gray.50', 'whiteAlpha.50');

  const [showExtendForm, setShowExtendForm] = useState(false);
  const [showChangePlanForm, setShowChangePlanForm] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [showResetToTrialForm, setShowResetToTrialForm] = useState(false);
  const [extendDate, setExtendDate] = useState('');
  const [extendReason, setExtendReason] = useState('');
  const [validationError, setValidationError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [changePlanReason, setChangePlanReason] = useState('');
  const [changePlanError, setChangePlanError] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [resetDate, setResetDate] = useState('');
  const [resetReason, setResetReason] = useState('');
  const [resetError, setResetError] = useState('');

  const queryKey = ['admin-subscription', organizationId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => apiClient.get<SubscriptionView>(`/admin/organizations/${organizationId}/subscription`),
  });

  const extendMutation = useMutation({
    mutationFn: (body: { newTrialEndDate: string; reason: string }) =>
      apiClient.post(`/admin/organizations/${organizationId}/subscription/extend-trial`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setShowExtendForm(false);
      setExtendDate('');
      setExtendReason('');
      setValidationError('');
      toast({ title: t('admin.subscription.extendSuccess'), status: 'success', duration: 3000 });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/admin/organizations/${organizationId}/subscription/reactivate-trial`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: t('admin.subscription.reactivateSuccess'), status: 'success', duration: 3000 });
    },
  });

  const changePlanMutation = useMutation({
    mutationFn: (body: { planId: string; reason: string }) =>
      apiClient.post(`/admin/organizations/${organizationId}/subscription/change-plan`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setShowChangePlanForm(false);
      setSelectedPlan('');
      setChangePlanReason('');
      setChangePlanError('');
      toast({ title: t('admin.subscription.changePlanSuccess'), status: 'success', duration: 3000 });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (body: { reason: string }) =>
      apiClient.post(`/admin/organizations/${organizationId}/subscription/cancel`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setShowCancelForm(false);
      setCancelReason('');
      setCancelError('');
      toast({ title: t('admin.subscription.cancelSuccess'), status: 'success', duration: 3000 });
    },
  });

  const resetToTrialMutation = useMutation({
    mutationFn: (body: { newTrialEndDate: string; reason: string }) =>
      apiClient.post(`/admin/organizations/${organizationId}/subscription/reset-to-trial`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setShowResetToTrialForm(false);
      setResetDate('');
      setResetReason('');
      setResetError('');
      toast({ title: t('admin.subscription.resetToTrialSuccess'), status: 'success', duration: 3000 });
    },
  });

  const enableTrialMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/admin/organizations/${organizationId}/subscription/enable-trial`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['admin-organization', organizationId] });
      toast({ title: t('admin.subscription.enableTrialSuccess'), status: 'success', duration: 3000 });
    },
  });

  function closeAllForms() {
    setShowExtendForm(false);
    setShowChangePlanForm(false);
    setShowCancelForm(false);
    setShowResetToTrialForm(false);
  }

  function handleExtendToggle() {
    const next = !showExtendForm;
    closeAllForms();
    setShowExtendForm(next);
    setValidationError('');
  }

  function handleChangePlanToggle() {
    const next = !showChangePlanForm;
    closeAllForms();
    setShowChangePlanForm(next);
    setChangePlanError('');
  }

  function handleCancelToggle() {
    const next = !showCancelForm;
    closeAllForms();
    setShowCancelForm(next);
    setCancelError('');
  }

  function handleResetToTrialToggle() {
    const next = !showResetToTrialForm;
    closeAllForms();
    setShowResetToTrialForm(next);
    setResetError('');
  }

  function handleExtendSubmit() {
    if (!extendReason.trim()) {
      setValidationError(t('admin.subscription.reasonRequired'));
      return;
    }
    if (!extendDate) return;
    extendMutation.mutate({ newTrialEndDate: new Date(extendDate).toISOString(), reason: extendReason });
  }

  function handleChangePlanSubmit() {
    if (!changePlanReason.trim()) {
      setChangePlanError(t('admin.subscription.reasonRequired'));
      return;
    }
    if (!selectedPlan) return;
    changePlanMutation.mutate({ planId: selectedPlan, reason: changePlanReason });
  }

  function handleCancelSubmit() {
    if (!cancelReason.trim()) {
      setCancelError(t('admin.subscription.reasonRequired'));
      return;
    }
    cancelMutation.mutate({ reason: cancelReason });
  }

  function handleResetToTrialSubmit() {
    if (!resetReason.trim()) {
      setResetError(t('admin.subscription.reasonRequired'));
      return;
    }
    if (!resetDate) return;
    resetToTrialMutation.mutate({ newTrialEndDate: new Date(resetDate).toISOString(), reason: resetReason });
  }

  if (isLoading) return <Spinner size="sm" />;
  if (!data) return (
    <VStack spacing={3} align="stretch" data-testid="subscription-panel-empty">
      <Text color={secondaryTextColor}>{t('admin.subscription.noData')}</Text>
      <Button
        colorScheme="blue"
        size="sm"
        onClick={() => enableTrialMutation.mutate()}
        isLoading={enableTrialMutation.isPending}
        data-testid="enable-trial-button"
      >
        {t('admin.subscription.enableTrial')}
      </Button>
    </VStack>
  );

  const isTrialing = TRIAL_STATUSES.includes(data.status);
  const canReactivate = REACTIVATABLE_STATUSES.includes(data.status);
  const atMaxExtensions = (data.trialExtensionCount ?? 0) >= MAX_EXTENSIONS;
  const isCanceled = data.status === 'canceled';

  return (
    <VStack spacing={4} align="stretch" data-testid="subscription-panel">

      {/* Common info */}
      <SimpleGrid columns={{ base: 1, md: 3 }} gap="20px" mb="16px">
        <Box>
          <Text fontSize="sm" color={secondaryTextColor} mb="4px">
            {t('admin.subscription.plan')}
          </Text>
          <Text color={textColor} fontWeight="500">
            {data.plan?.charAt(0).toUpperCase() + data.plan?.slice(1)}
          </Text>
        </Box>
        <Box>
          <Text fontSize="sm" color={secondaryTextColor} mb="4px">
            {t('admin.subscription.status')}
          </Text>
          <Badge colorScheme={getStatusColor(data.status)} fontSize="xs" borderRadius="8px" px="10px" data-testid="status-badge">
            {data.status}
          </Badge>
        </Box>
        {data.currentPeriodEnd && (
          <Box>
            <Text fontSize="sm" color={secondaryTextColor} mb="4px">
              {t('admin.subscription.currentPeriodEnd')}
            </Text>
            <Text color={textColor} fontWeight="500">
              {new Date(data.currentPeriodEnd).toLocaleDateString()}
            </Text>
          </Box>
        )}
      </SimpleGrid>

      {/* Trial-specific info */}
      {isTrialing && (
        <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px" mb="16px" data-testid="trial-info">
          {data.trialEndsAt && (
            <Box>
              <Text fontSize="sm" color={secondaryTextColor} mb="4px">
                {t('admin.subscription.trialEndDate')}
              </Text>
              <Text color={textColor} fontWeight="500">
                {new Date(data.trialEndsAt).toLocaleDateString()}
              </Text>
            </Box>
          )}
          <Box>
            <Text fontSize="sm" color={secondaryTextColor} mb="4px">
              {t('admin.subscription.extensionCount')}
            </Text>
            <Text color={textColor} fontWeight="500">{data.trialExtensionCount ?? 0}</Text>
          </Box>
        </SimpleGrid>
      )}

      {/* Canceled info */}
      {isCanceled && data.canceledAt && (
        <Box mb="16px" data-testid="canceled-info">
          <Text fontSize="sm" color={secondaryTextColor} mb="4px">
            {t('admin.subscription.canceledAt')}
          </Text>
          <Text color={textColor} fontWeight="500">
            {new Date(data.canceledAt).toLocaleDateString()}
          </Text>
        </Box>
      )}

      {/* Action buttons */}
      <Flex gap="10px" mt="8px" mb="16px" flexWrap="wrap">
        {isTrialing && (
          <Button size="sm" variant="brand" onClick={handleExtendToggle} data-testid="btn-extend-trial">
            {t('admin.subscription.extendTrial')}
          </Button>
        )}
        {!isCanceled && (
          <Button size="sm" variant="brand" onClick={handleChangePlanToggle} data-testid="btn-change-plan">
            {t('admin.subscriptions.changePlan')}
          </Button>
        )}
        {!isCanceled && !isTrialing && (
          <Button size="sm" colorScheme="orange" onClick={handleResetToTrialToggle} data-testid="btn-reset-to-trial">
            {t('admin.subscription.resetToTrial')}
          </Button>
        )}
        {!isCanceled && (
          <Button size="sm" colorScheme="red" onClick={handleCancelToggle} data-testid="btn-cancel-subscription">
            {t('admin.subscriptions.cancel')}
          </Button>
        )}
        {canReactivate && (
          <Button
            size="sm"
            colorScheme="green"
            onClick={() => reactivateMutation.mutate()}
            isLoading={reactivateMutation.isPending}
            data-testid="btn-reactivate-trial"
          >
            {t('admin.subscription.reactivateTrial')}
          </Button>
        )}
      </Flex>

      {/* Extend trial form — only for trialing */}
      {showExtendForm && isTrialing && (
        <Box p="16px" bg={formBg} borderRadius="15px" mt="8px">
          {atMaxExtensions ? (
            <Text color="red.500">{t('admin.subscription.maxExtensions')}</Text>
          ) : (
            <VStack spacing={3} align="stretch">
              <FormControl mb="12px">
                <FormLabel fontSize="sm" color={textColor} fontWeight="bold" ms="10px">
                  {t('admin.subscription.newTrialEndDate')}
                </FormLabel>
                <Input
                  size="sm"
                  type="date"
                  borderRadius="16px"
                  value={extendDate}
                  onChange={(e) => setExtendDate(e.target.value)}
                  data-testid="extend-trial-date"
                />
              </FormControl>
              <FormControl isInvalid={Boolean(validationError)}>
                <FormLabel fontSize="sm" color={textColor} fontWeight="bold" ms="10px">
                  {t('admin.subscription.reason')}
                </FormLabel>
                <Textarea
                  size="sm"
                  borderRadius="16px"
                  value={extendReason}
                  onChange={(e) => {
                    setExtendReason(e.target.value);
                    if (validationError) setValidationError('');
                  }}
                  data-testid="extend-trial-reason"
                  rows={2}
                />
                {validationError && <FormErrorMessage>{validationError}</FormErrorMessage>}
              </FormControl>
              <Button
                size="sm"
                variant="brand"
                onClick={handleExtendSubmit}
                isLoading={extendMutation.isPending}
                data-testid="extend-trial-submit"
              >
                {t('admin.subscription.extendTrial')}
              </Button>
            </VStack>
          )}
        </Box>
      )}

      {/* Change plan form */}
      {showChangePlanForm && (
        <Box p="16px" bg={formBg} borderRadius="15px" mt="8px">
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm" color={textColor} fontWeight="bold" ms="10px">
                {t('admin.subscription.newPlan')}
              </FormLabel>
              <Select
                size="sm"
                borderRadius="16px"
                placeholder={t('admin.subscription.selectPlan')}
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                data-testid="change-plan-select"
              >
                {AVAILABLE_PLANS.filter((p) => p !== data.plan).map((plan) => (
                  <option key={plan} value={plan}>
                    {plan.charAt(0).toUpperCase() + plan.slice(1)}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isInvalid={Boolean(changePlanError)}>
              <FormLabel fontSize="sm" color={textColor} fontWeight="bold" ms="10px">
                {t('admin.subscription.reason')}
              </FormLabel>
              <Textarea
                size="sm"
                borderRadius="16px"
                value={changePlanReason}
                onChange={(e) => {
                  setChangePlanReason(e.target.value);
                  if (changePlanError) setChangePlanError('');
                }}
                data-testid="change-plan-reason"
                rows={2}
              />
              {changePlanError && <FormErrorMessage>{changePlanError}</FormErrorMessage>}
            </FormControl>
            <Button
              size="sm"
              variant="brand"
              onClick={handleChangePlanSubmit}
              isLoading={changePlanMutation.isPending}
              data-testid="change-plan-submit"
            >
              {t('admin.subscriptions.changePlan')}
            </Button>
          </VStack>
        </Box>
      )}

      {/* Cancel subscription form */}
      {showCancelForm && (
        <Box p="16px" bg={formBg} borderRadius="15px" mt="8px" data-testid="cancel-form">
          <VStack spacing={3} align="stretch">
            <FormControl isInvalid={Boolean(cancelError)}>
              <FormLabel fontSize="sm" color={textColor} fontWeight="bold" ms="10px">
                {t('admin.subscription.reason')}
              </FormLabel>
              <Textarea
                size="sm"
                borderRadius="16px"
                value={cancelReason}
                onChange={(e) => {
                  setCancelReason(e.target.value);
                  if (cancelError) setCancelError('');
                }}
                data-testid="cancel-reason"
                rows={2}
              />
              {cancelError && <FormErrorMessage>{cancelError}</FormErrorMessage>}
            </FormControl>
            <Button
              size="sm"
              colorScheme="red"
              onClick={handleCancelSubmit}
              isLoading={cancelMutation.isPending}
              data-testid="cancel-submit"
            >
              {t('admin.subscription.confirmCancel')}
            </Button>
          </VStack>
        </Box>
      )}

      {/* Reset to trial form */}
      {showResetToTrialForm && (
        <Box p="16px" bg={formBg} borderRadius="15px" mt="8px" data-testid="reset-to-trial-form">
          <VStack spacing={3} align="stretch">
            <FormControl mb="12px">
              <FormLabel fontSize="sm" color={textColor} fontWeight="bold" ms="10px">
                {t('admin.subscription.newTrialEndDate')}
              </FormLabel>
              <Input
                size="sm"
                type="date"
                borderRadius="16px"
                value={resetDate}
                onChange={(e) => setResetDate(e.target.value)}
                data-testid="reset-trial-date"
              />
            </FormControl>
            <FormControl isInvalid={Boolean(resetError)}>
              <FormLabel fontSize="sm" color={textColor} fontWeight="bold" ms="10px">
                {t('admin.subscription.reason')}
              </FormLabel>
              <Textarea
                size="sm"
                borderRadius="16px"
                value={resetReason}
                onChange={(e) => {
                  setResetReason(e.target.value);
                  if (resetError) setResetError('');
                }}
                data-testid="reset-trial-reason"
                rows={2}
              />
              {resetError && <FormErrorMessage>{resetError}</FormErrorMessage>}
            </FormControl>
            <Button
              size="sm"
              colorScheme="orange"
              onClick={handleResetToTrialSubmit}
              isLoading={resetToTrialMutation.isPending}
              data-testid="reset-trial-submit"
            >
              {t('admin.subscription.resetToTrial')}
            </Button>
          </VStack>
        </Box>
      )}

      {/* Payment History */}
      {data.paymentHistory?.length > 0 && (
        <Box mt="16px">
          <Text fontSize="md" fontWeight="700" color={textColor} mb="12px">
            {t('admin.subscription.paymentHistory')}
          </Text>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th color={secondaryTextColor} borderColor={borderColor}>{t('admin.subscription.date')}</Th>
                <Th color={secondaryTextColor} borderColor={borderColor}>{t('admin.subscription.amount')}</Th>
                <Th color={secondaryTextColor} borderColor={borderColor}>{t('admin.subscription.status')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.paymentHistory.map((p, i) => (
                <Tr key={i}>
                  <Td borderColor={borderColor}>
                    <Text color={textColor} fontSize="sm">{new Date(p.date).toLocaleDateString()}</Text>
                  </Td>
                  <Td borderColor={borderColor}>
                    <Text color={textColor} fontSize="sm">${p.amount}</Text>
                  </Td>
                  <Td borderColor={borderColor}>
                    <Badge colorScheme={p.status === 'paid' ? 'green' : 'gray'} fontSize="xs" borderRadius="8px" px="8px">
                      {p.status}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </VStack>
  );
}
